import {observable} from 'mobx';

import {City} from '@/domain/models/City';
import {Country} from '@/domain/models/Country';
import {User} from '@/domain/models/User';
import {isLocationVerified} from '@/domain/models/VerifiedLocation';

class GeoStore {
  readonly cities = observable.map<City['id'], City>();
  readonly countries = observable.map<Country['id'], Country>();

  cleanUp() {
    this.cities.clear();
  }

  setCities(cities: City[]) {
    this.cities.replace(cities.map((city) => [city.id, city]));
  }

  deleteCityById(id: number) {
    this.cities.delete(id);
  }

  addCities(cities?: City[]) {
    if (!cities || cities.length === 0) {
      return;
    }

    this.cities.merge(cities.map((city) => [city.id, city]));
  }

  addCity(city?: City) {
    if (!city || this.cities.has(city.id)) {
      return;
    }

    this.cities.set(city.id, city);
  }

  getCityById(id?: number) {
    if (!id) {
      return undefined;
    }

    return this.cities.get(id);
  }

  getCountryById(id?: number) {
    if (!id) {
      return undefined;
    }

    return this.countries.get(id);
  }

  getCountryByCode(code?: string) {
    if (!code) {
      return undefined;
    }

    for (const country of this.countries.values()) {
      if (country.code === code) {
        return country;
      }
    }

    return undefined;
  }

  addCountries(countries?: Country[]) {
    if (!countries || countries.length === 0) {
      return;
    }

    this.countries.merge(countries.map((country) => [country.id, country]));
  }

  getLocationVerificationByUser(user?: User | null) {
    if (!user) {
      return {
        isVerified: false,
      };
    }

    const city = this.getCityById(user.city_id);
    const country =
      this.getCountryById(city?.country_id) ??
      this.getCountryByCode(user.country_code);
    const cityName = city?.name;
    const countryCode = user.country_code ?? country?.code;
    const countryName = country?.name;
    const hasPlace = Boolean(cityName || countryCode || countryName);

    return {
      isVerified: isLocationVerified(user),
      place: hasPlace
        ? {
            cityName,
            countryCode,
            countryName,
          }
        : undefined,
    };
  }
}

export default new GeoStore();
